/* eslint-disable react/jsx-no-literals */
/* eslint-disable no-magic-numbers */
import React from 'react';
import Typography from 'carbon-react/lib/components/typography';
import Box from 'carbon-react/lib/components/box';
import { Card } from 'carbon-react/lib/components/card';
import Message from 'carbon-react/lib/components/message';
import { useIntl } from 'react-intl';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line } from 'react-chartjs-2';

import * as styles from './revenue-trend-chart.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ChartDataLabels);

const ACTUAL_COLOR = '#00815D';
const FORECAST_COLOR = '#C5D1D6';

export interface ChartWarningMessage {
  title: string;
  content: string;
  variant: 'warning' | 'error' | 'info' | 'success';
}

export interface RevenueTrendChartProps {
  title: string;
  subtitle?: string;
  months: string[];
  actuals: number[];
  forecast: number[];
  yAxisPrefix?: string;
  warningMessage?: ChartWarningMessage;
}

export const RevenueTrendChart = ({
  title,
  subtitle,
  months,
  actuals,
  forecast,
  yAxisPrefix = '$',
  warningMessage
}: RevenueTrendChartProps) => {
  const intl = useIntl();

  /* Actual line ends at last actual; forecast starts at last actual (shared point) for continuity */
  const actualData: (number | null)[] = [
    ...actuals,
    ...Array(forecast.length).fill(null)
  ];
  const forecastData: (number | null)[] = forecast.length > 0
    ? [
      ...Array(Math.max(actuals.length - 1, 0)).fill(null),
      actuals.length > 0 ? actuals[actuals.length - 1] : null,
      ...forecast
    ]
    : [];

  const datasets = [
    {
      label: intl.formatMessage({ id: 'aiInsights.chart.actual', defaultMessage: 'Actual' }),
      data: actualData,
      borderColor: ACTUAL_COLOR,
      backgroundColor: 'rgba(0, 129, 93, 0.08)',
      pointBackgroundColor: ACTUAL_COLOR,
      pointRadius: 3,
      pointHoverRadius: 5,
      borderWidth: 2.5,
      tension: 0.35,
      fill: true,
      spanGaps: false
    },
    ...(forecast.length > 0
      ? [{
        label: intl.formatMessage({ id: 'aiInsights.chart.forecast', defaultMessage: 'Forecast' }),
        data: forecastData,
        borderColor: FORECAST_COLOR,
        backgroundColor: 'rgba(197, 209, 214, 0.12)',
        pointBackgroundColor: FORECAST_COLOR,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2.5,
        borderDash: [6, 4],
        tension: 0.35,
        fill: true,
        spanGaps: false
      }]
      : [])
  ];

  const data = {
    labels: months,
    datasets
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        align: 'start' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 24,
          font: { family: 'SageUI', size: 12, weight: '400' as const },
          color: 'rgba(0, 0, 0, 0.90)',
          generateLabels: (chart: ChartJS) =>
            chart.data.datasets.map((dataset, i) => {
              const meta = chart.getDatasetMeta(i);
              const hidden = meta.hidden ?? false;
              return {
                text: dataset.label ?? '',
                fillStyle: hidden ? 'transparent' : (dataset.borderColor as string),
                strokeStyle: dataset.borderColor as string,
                lineWidth: 2,
                pointStyle: 'circle' as const,
                hidden: false,
                datasetIndex: i
              };
            })
        }
      },
      tooltip: { enabled: true },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: '#7C879D',
          font: { family: 'SageUI', size: 10, weight: '500' as const },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        grid: {
          drawBorder: false,
          drawTicks: false,
          color: '#DFE3EC'
        },
        ticks: {
          color: '#7C879D',
          font: { family: 'SageUI', size: 10, weight: '500' as const },
          callback: (value: number | string) => `${yAxisPrefix}${Number(value).toLocaleString()}`
        },
        beginAtZero: false
      }
    }
  };

  return (
    <div className={styles.cardContainer}>
      <Card roundness="large" width="100%" m={0}>
        <Box display="flex" width="100%">
          <Box p={4} width="100%">
            <Typography fontSize="21px" fontWeight="500" lineHeight="125%" mb={subtitle ? 0 : 3}>
              {title}
            </Typography>
            {subtitle && (
              <Typography fontSize="14px" fontWeight="400" lineHeight="150%" mb={3} color="var(--colorsUtilityYin055)">
                {subtitle}
              </Typography>
            )}
            <div className={styles.chartContainer}>
              <Line data={data} options={options} aria-label={intl.formatMessage({ id: 'aiInsights.chart.ariaLabel', defaultMessage: 'Revenue Trend Chart' })} />
            </div>
            {warningMessage && (
              <Box mt={3}>
                <Message title={warningMessage.title} variant={warningMessage.variant} open showCloseIcon={false}>
                  {warningMessage.content}
                </Message>
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    </div>
  );
};
